import { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import * as am5radar from '@amcharts/amcharts5/radar'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'
import { format } from 'date-fns'
import './index.css'

const HOUR_LABELS = Array.from({ length: 24 }, (_, i) => format(new Date(2000, 0, 1, i), 'h aaa'))

const DashboardLogsActionsByTime = ({ chartID, dataSet }) => {
  useLayoutEffect(() => {
    // Build 24-slot dataset; fill missing hours with 0
    const countByHour = {}
    ;(dataSet.byTime || []).forEach((item) => {
      countByHour[item.hour] = item.count
    })
    const chartData = HOUR_LABELS.map((label, i) => ({
      time: label,
      count: countByHour[i] || 0
    }))

    const root = am5.Root.new(chartID)
    root.setThemes([am5themes_Animated.new(root)])
    root._logo.dispose()

    // Radar (polar) chart — clock layout
    const chart = root.container.children.push(
      am5radar.RadarChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        innerRadius: am5.percent(25),
        radius: am5.percent(90)
      })
    )

    // X axis — hours around the circle
    const xRenderer = am5radar.AxisRendererCircular.new(root, {
      minGridDistance: 15
    })
    xRenderer.labels.template.setAll({
      fontSize: 9,
      radius: 8,
      fill: am5.color(0x555555)
    })
    xRenderer.grid.template.set('forceHidden', true)

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'time',
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {})
      })
    )

    // Y axis — count (radial)
    const yRenderer = am5radar.AxisRendererRadial.new(root, {})
    yRenderer.labels.template.set('forceHidden', true)
    yRenderer.grid.template.setAll({ strokeOpacity: 0.08 })

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        strictMinMax: true,
        maxPrecision: 0,
        renderer: yRenderer
      })
    )

    // Radar column series
    const series = chart.series.push(
      am5radar.RadarColumnSeries.new(root, {
        xAxis,
        yAxis,
        categoryXField: 'time',
        valueYField: 'count',
        tooltip: am5.Tooltip.new(root, {
          labelText: '[bold]{categoryX}[/]\n{valueY} actions'
        })
      })
    )

    series.columns.template.setAll({
      width: am5.percent(88),
      cornerRadiusTL: 3,
      cornerRadiusTR: 3,
      strokeOpacity: 0,
      fillOpacity: 0.85
    })

    // Heat colour: low count = steel blue, high count = deep indigo
    series.columns.template.adapters.add('fill', (_fill, target) => {
      const item = target.dataItem
      if (!item) return am5.color(0x5a7aa6)
      const max = Math.max(...chartData.map((d) => d.count), 1)
      const ratio = item.get('valueY') / max
      const r = Math.round(0x5a + ratio * (0x2c - 0x5a))
      const g = Math.round(0x7a + ratio * (0x4a - 0x7a))
      const b = Math.round(0xa6 + ratio * (0xd0 - 0xa6))
      return am5.color((r << 16) | (g << 8) | b)
    })

    xAxis.data.setAll(chartData)
    series.data.setAll(chartData)

    series.appear(1000)
    chart.appear(1000, 100)

    // ResizeObserver keeps chart correctly sized after layout changes
    const container = document.getElementById(chartID)
    const ro = new ResizeObserver(() => root.resize())
    if (container) ro.observe(container)

    return () => {
      ro.disconnect()
      root.dispose()
    }
  }, [chartID, dataSet])

  return <div id={chartID} className='logs-actions-time-chart' />
}

DashboardLogsActionsByTime.propTypes = {
  chartID: PropTypes.string.isRequired,
  dataSet: PropTypes.object.isRequired
}

export default DashboardLogsActionsByTime
